import { fetchWithDefaults } from '@/lib/http/client';
import type { ConversationLifecycleState } from '@/lib/reasoning/conversationLifecycleState';

interface ReasoningStep {
  id: number;
  step_number: number;
  stage: string;
  content: string;
  is_final: boolean;
  status: string;
  tokens_used?: number;
  processing_time_ms?: number;
  created_at: string;
  artifacts?: Array<{
    id: number;
    title: string;
    artifact_type: { name: string; description: string };
    status: string;
    version_number: number;
    created_at: string;
  }>;
  created_actions?: number[]; // IDs of actions created/updated by this step
  created_proposals?: number[]; // IDs of proposals created by this step
}

interface ReasoningChain {
  id: number;
  chain_number: number;
  user_message: string;
  status: string;
  created_at: string;
  steps: ReasoningStep[];
  step_limit_reached?: boolean;
  task_id?: string | null;
  processing_status?: string;
}

interface ConversationDetails {
  conversation: {
    id: number;
    title: string;
    description: string;
    state: ConversationLifecycleState;
    created_at: string;
    updated_at: string;
  };
  chains: ReasoningChain[];
}

interface ReportConversationListItem {
  id: number;
  title: string;
  initial_prompt: string;
  state: ConversationLifecycleState;
  total_steps: number;
  created_at: string;
  builder_entity_type: string | null;
  builder_entity_id: string | null;
  has_active_chain: boolean;
}

class ConversationService {
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorData}`);
    }
    return response.json();
  }

  /**
   * Get conversation details including chains and steps
   */
  async getConversationDetails(
    orgId: number,
    conversationId: number
  ): Promise<ConversationDetails> {
    const response = await fetchWithDefaults(
      `/api/orgs/${orgId}/reasoning/conversations/${conversationId}/`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return this.handleResponse<ConversationDetails>(response);
  }

  /**
   * List the caller's report conversations (via the ?intent=report anchor)
   */
  async listReportConversations(
    orgId: number
  ): Promise<{ conversations: ReportConversationListItem[] }> {
    const response = await fetchWithDefaults(
      `/api/orgs/${orgId}/reasoning/conversations/?intent=report`,
      { headers: { 'Content-Type': 'application/json' } }
    );
    return this.handleResponse<{ conversations: ReportConversationListItem[] }>(
      response
    );
  }

  /**
   * Regenerate a chain (create a new response for the same user message)
   * Only allowed for the last chain in a conversation when it's completed or failed
   */
  async regenerateChain(
    orgId: string,
    conversationId: string,
    chainId: string
  ): Promise<{ chain_id: string; task_id: string }> {
    const response = await fetchWithDefaults(
      `/api/orgs/${orgId}/reasoning/conversations/${conversationId}/chains/${chainId}/regenerate/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return this.handleResponse<{ chain_id: string; task_id: string }>(response);
  }
}

export const conversationService = new ConversationService();

// Export types for use in components
export type {
  ConversationDetails,
  ReasoningChain,
  ReasoningStep,
  ReportConversationListItem,
};
